import React, { useState } from 'react';
import { NodeProps } from '@xyflow/react';
import useLocale from 'next-translate/useTranslation';
import { BaseNode, createOutputHandle } from './BaseNode';
import { useWorkflowStore } from '@/lib/workflows/store';
import { NodeExecutionStatus } from 'types';
import Spinner from '@/components/Spinner';
import { CheckIcon } from '@heroicons/react/solid';
import Modal from '@/components/atoms/Modal';
import clsxm from '@/lib/clsxm';
import { NodeActionsDropdown } from './NodeActionsDropdown';
import EditModal from '@/components/images/EditModal';
import { useIsMobile } from '@/hooks/useIsMobile';

interface SelectImageNodeProps extends NodeProps {
  nodeId?: string;
  readOnly?: boolean;
}

interface ImageItem {
  id: number;
  image_url: string;
  prompt?: string;
  model?: string;
  created_at: string;
  is_video: boolean;
}

export function SelectImageNode({
  id,
  data,
  selected,
  nodeId,
  readOnly,
}: SelectImageNodeProps) {
  const { t } = useLocale('common');
  const isMobile = useIsMobile();
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [videos, setVideos] = useState<ImageItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentVideoPage, setCurrentVideoPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreVideos, setHasMoreVideos] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState<'images' | 'videos'>('images');
  const [isLandscape, setIsLandscape] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const config = (data.config || {}) as {
    selectedImageUrl?: string;
    selectedIsVideo?: boolean;
    selectedImageId?: number;
  };
  const selectedImageUrl = config.selectedImageUrl || '';
  const selectedIsVideo = Boolean(config.selectedIsVideo);

  const IMAGES_PER_PAGE = 50;
  const VIDEOS_PER_PAGE = 50;

  const fetchImages = async (page: number, append: boolean = false) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/images/paginated?page=${page}&limit=${IMAGES_PER_PAGE}`,
      );
      if (!response.ok) {
        throw new Error('Failed to fetch images');
      }
      const result = await response.json();
      const imageData = (result.data || []) as ImageItem[];

      if (append) {
        const newImages = [...images, ...imageData];
        setImages(newImages);
        // Check if we have more images based on total count
        setHasMore(newImages.length < totalCount);
      } else {
        setImages(imageData);
        // Set total count from API response (count of non-video images)
        const totalImageCount = result.count || 0;
        setTotalCount(totalImageCount);
        setHasMore(imageData.length < totalImageCount);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      if (!append) {
        setImages([]);
        setTotalCount(0);
      }
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVideos = async (page: number = 1, append: boolean = false) => {
    setIsLoading(true);
    try {
      // Calculate offset and fetch one extra to check if there are more
      const offset = (page - 1) * VIDEOS_PER_PAGE;
      const limitToFetch = VIDEOS_PER_PAGE + 1;
      const response = await fetch(
        `/api/get-videos?limit=${limitToFetch}&offset=${offset}`,
      );
      if (!response.ok) {
        throw new Error('Failed to fetch videos');
      }
      const videoData = await response.json();

      // Check if there are more videos
      const hasMoreData = videoData.length > VIDEOS_PER_PAGE;
      const videosToShow = hasMoreData
        ? videoData.slice(0, VIDEOS_PER_PAGE)
        : videoData;

      // Map the video data to match ImageItem interface
      const mappedVideos = (videosToShow || []).map((video: any) => ({
        id: video.id,
        image_url: video.video_url || video.image_url,
        prompt: video.prompt,
        model: video.model,
        created_at: video.created_at,
        is_video: true,
      }));

      if (append) {
        const newVideos = [...videos, ...mappedVideos];
        setVideos(newVideos);
        setHasMoreVideos(hasMoreData);
      } else {
        setVideos(mappedVideos);
        setHasMoreVideos(hasMoreData);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      if (!append) {
        setVideos([]);
      }
      setHasMoreVideos(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectImage = (image: ImageItem) => {
    if (readOnly) return;
    updateNodeData(id, {
      config: {
        selectedIsVideo: image.is_video,
        selectedImageUrl: image.image_url,
        selectedImageId: image.id,
      },
      result: image.image_url,
    });
    setShowGallery(false);
  };

  const handleOpenGallery = () => {
    if (readOnly) return;
    setShowGallery(true);
    if (activeTab === 'images' && images.length === 0) {
      setCurrentPage(1);
      fetchImages(1, false);
    } else if (activeTab === 'videos' && videos.length === 0) {
      setCurrentVideoPage(1);
      fetchVideos(1, false);
    }
  };

  const handleTabChange = (tab: 'images' | 'videos') => {
    setActiveTab(tab);

    // Only fetch data if switching to a tab with no data loaded yet
    if (tab === 'images' && images.length === 0) {
      setCurrentPage(1);
      setHasMore(true);
      setTotalCount(0);
      fetchImages(1, false);
    } else if (tab === 'videos' && videos.length === 0) {
      setCurrentVideoPage(1);
      setHasMoreVideos(true);
      fetchVideos(1, false);
    }
    // If data is already loaded, don't reset pagination state
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchImages(nextPage, true);
  };

  const handleLoadMoreVideos = () => {
    const nextPage = currentVideoPage + 1;
    setCurrentVideoPage(nextPage);
    fetchVideos(nextPage, true);
  };

  const handleRemoveSelection = () => {
    updateNodeData(id, {
      config: {
        selectedIsVideo: false,
        selectedImageUrl: '',
        selectedImageId: undefined,
      },
      result: '',
    });
  };

  return (
    <>
      <BaseNode
        title={(data.label as string) || t('Select Media')}
        icon={
          <svg
            className='w-4 h-4 text-blue-600'
            fill='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' />
          </svg>
        }
        hasInput={false}
        hasOutput={false}
        status={data.status as NodeExecutionStatus}
        selected={selected}
        nodeId={nodeId || id}
        readOnly={readOnly}
        hideToolbarActions
        customHeaderButtons={
          <NodeActionsDropdown
            nodeId={nodeId || id}
            nodeTitle={(data.label as string) || t('Select Media')}
            mediaUrl={selectedImageUrl}
            mediaType={selectedIsVideo ? 'video' : 'image'}
            filename={
              selectedIsVideo ? 'selected-video.mp4' : 'selected-image.png'
            }
            readOnly={readOnly}
            onEditImage={
              selectedImageUrl && !selectedIsVideo
                ? () => setShowEditModal(true)
                : undefined
            }
          />
        }
        customOutputs={
          selectedIsVideo ? (
            <>
              {createOutputHandle('image', 'first-frame', 30)}
              {createOutputHandle('image', 'last-frame', 60)}
              {createOutputHandle('video', 'video', 90)}
            </>
          ) : (
            createOutputHandle('image', 'image', 50)
          )
        }
      >
        <div className={clsxm(selectedImageUrl ? '' : 'space-y-3 px-4 py-3')}>
          {/* Selected Media Preview */}
          {selectedImageUrl ? (
            <div className={clsxm(isLandscape ? 'w-[700px]' : 'w-[420px]')}>
              <div className='relative group rounded-md overflow-hidden'>
                {selectedIsVideo ? (
                  <video
                    src={selectedImageUrl}
                    controls
                    className='w-full h-auto object-cover'
                    preload='metadata'
                    autoPlay={!isMobile}
                    muted
                    playsInline
                    loop
                    onLoadedMetadata={(e) => {
                      const video = e.currentTarget;
                      setIsLandscape(video.videoWidth > video.videoHeight);
                    }}
                  />
                ) : (
                  <img
                    src={selectedImageUrl}
                    alt='Selected'
                    className='w-full h-auto object-cover'
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      setIsLandscape(img.naturalWidth > img.naturalHeight);
                    }}
                  />
                )}
                <button
                  onClick={handleRemoveSelection}
                  className='absolute right-2 top-2 rounded-full bg-red-500 p-2 shadow-lg hover:bg-red-600 transition-all'
                  title={t('Remove selection')}
                >
                  <svg
                    className='h-5 w-5 text-white'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path d='M6 18L18 6M6 6l12 12' />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            /* Select Button */
            <button
              onClick={handleOpenGallery}
              className='w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg border-2 border-blue-200 hover:border-blue-300 transition-colors text-sm font-medium'
              disabled={readOnly}
            >
              {t('Select from Gallery')}
            </button>
          )}

          {!selectedImageUrl && (
            <div className='text-xs text-gray-500 bg-gray-50 p-2 rounded'>
              {t('Select from your previously generated images or videos')}
            </div>
          )}

          {/* Image Gallery Modal */}
          <Modal
            showModal={showGallery}
            onClose={() => setShowGallery(false)}
            className='sm:max-w-4xl'
          >
            <div className='p-6'>
              <h3 className='text-lg font-semibold text-gray-900 mb-4'>
                {t('Select Media')}
              </h3>

              {/* Tab Navigation */}
              <div className='flex gap-2 mb-4 border-b border-gray-200'>
                <button
                  onClick={() => handleTabChange('images')}
                  className={clsxm(
                    'px-4 py-2 text-sm font-medium transition-colors border-b-2',
                    activeTab === 'images'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700',
                  )}
                >
                  {t('Images')}
                  {images.length > 0 && (
                    <span className='ml-1.5 text-xs'>({images.length})</span>
                  )}
                </button>
                <button
                  onClick={() => handleTabChange('videos')}
                  className={clsxm(
                    'px-4 py-2 text-sm font-medium transition-colors border-b-2',
                    activeTab === 'videos'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700',
                  )}
                >
                  {t('Videos')}
                  {videos.length > 0 && (
                    <span className='ml-1.5 text-xs'>({videos.length})</span>
                  )}
                </button>
              </div>

              {(activeTab === 'images' ? images : videos).length === 0 &&
              isLoading ? (
                <div className='flex items-center justify-center h-64'>
                  <Spinner className='h-10 w-10 text-blue-500' />
                </div>
              ) : (activeTab === 'images' ? images : videos).length > 0 ? (
                <>
                  <div className='grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 max-h-[60vh] overflow-y-auto pr-2'>
                    {(activeTab === 'images' ? images : videos).map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleSelectImage(item)}
                        className='relative aspect-square rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all group'
                        title={
                          item.prompt ||
                          (item.is_video
                            ? t('Generated video')
                            : t('Generated image'))
                        }
                      >
                        {item.is_video && (
                          <div className='absolute top-1 left-1 z-10 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1'>
                            <svg
                              width='10'
                              height='10'
                              viewBox='0 0 24 24'
                              fill='currentColor'
                              className='opacity-90'
                              aria-hidden='true'
                            >
                              <path d='M8 5v14l11-7z'></path>
                            </svg>
                            Video
                          </div>
                        )}
                        {item.is_video ? (
                          <video
                            src={item.image_url}
                            className='w-full h-full object-cover bg-black'
                            preload='metadata'
                            autoPlay={!isMobile}
                            muted
                            playsInline
                            loop
                          />
                        ) : (
                          <img
                            src={item.image_url}
                            alt={item.prompt || t('Image')}
                            className='w-full h-full object-cover'
                          />
                        )}
                        <div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center'>
                          <CheckIcon className='h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity' />
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Load More Button */}
                  {((activeTab === 'images' && hasMore) ||
                    (activeTab === 'videos' && hasMoreVideos)) && (
                    <div className='mt-4 flex justify-center'>
                      <button
                        onClick={
                          activeTab === 'images'
                            ? handleLoadMore
                            : handleLoadMoreVideos
                        }
                        disabled={isLoading}
                        className='px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                      >
                        {isLoading ? (
                          <span className='flex items-center gap-2'>
                            <Spinner className='h-4 w-4' />
                            {t('Loading...')}
                          </span>
                        ) : (
                          t('Load More')
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className='text-sm text-gray-500 text-center py-16'>
                  {activeTab === 'images'
                    ? t('No images found. Generate some images first!')
                    : t('No videos found. Generate some videos first!')}
                </div>
              )}
            </div>
          </Modal>
        </div>
      </BaseNode>
      {selectedImageUrl && !selectedIsVideo && (
        <EditModal
          image={{ image_url: selectedImageUrl, id: config.selectedImageId }}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUpdate={(updatedImage) => {
            if (updatedImage.image_url) {
              updateNodeData(id, {
                config: { ...config, selectedImageUrl: updatedImage.image_url },
                result: updatedImage.image_url,
              });
            }
          }}
        />
      )}
    </>
  );
}
